"use client";

import { useEffect, useState } from "react";

import type { TocItem } from "@/utils/toc";

export function useActiveHeading(tocItems: TocItem[]) {
        const [activeId, setActiveId] = useState<string | null>(tocItems.at(0)?.id ?? null);

        useEffect(() => {
                setActiveId(tocItems.at(0)?.id ?? null);
        }, [tocItems]);

        useEffect(() => {
                if (!tocItems.length) return;

                const headingsElements = tocItems
                        .map((item) => document.getElementById(item.id))
                        .filter((element): element is HTMLElement => Boolean(element));

                if (!headingsElements.length) return;

                const observer = new IntersectionObserver(
                        (entries) => {
                                const visibleEntries = entries.filter((entry) => entry.isIntersecting);

                                if (!visibleEntries.length) return;

                                const topEntry = visibleEntries.reduce((prev, current) =>
                                        current.intersectionRatio > prev.intersectionRatio ? current : prev
                                );

                                setActiveId(topEntry.target.id);
                        },
                        {
                                rootMargin: "-40% 0px -50% 0px",
                                threshold: [0, 0.25, 0.5, 0.75, 1],
                        }
                );

                headingsElements.forEach((element) => observer.observe(element));

                return () => {
                        headingsElements.forEach((element) => observer.unobserve(element));
                        observer.disconnect();
                };
        }, [tocItems]);

        return { activeId, setActiveId };
}
